<template>
  <div
    class="flex flex-col sm:flex-row items-center sm:items-start gap-5 rounded-2xl border border-gray-100 bg-white shadow-sm p-6"
  >
    <div class="relative shrink-0" :style="{ width: svgSize + 'px', height: svgSize + 'px' }">
      <svg
        :width="svgSize"
        :height="svgSize"
        class="absolute top-0 left-0 -rotate-90"
        aria-hidden="true"
      >
        <circle
          :cx="svgSize / 2"
          :cy="svgSize / 2"
          :r="radius"
          :stroke="NEUTRAL_RING_COLOR"
          :stroke-width="STROKE_WIDTH"
          fill="none"
        />
        <circle
          v-if="clampedScore > 0"
          :cx="svgSize / 2"
          :cy="svgSize / 2"
          :r="radius"
          :stroke="arcColor"
          :stroke-width="STROKE_WIDTH"
          fill="none"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="offset"
          stroke-linecap="round"
        />
      </svg>
      <v-avatar
        color="primary"
        :size="AVATAR_SIZE"
        class="absolute"
        :style="{ top: STROKE_WIDTH + 'px', left: STROKE_WIDTH + 'px' }"
      >
        <ProtectedAvatar :src="avatarSrc" :size="AVATAR_SIZE">
          <span class="text-2xl font-bold">{{ initials }}</span>
        </ProtectedAvatar>
      </v-avatar>
    </div>

    <div class="flex flex-col items-center sm:items-start gap-2">
      <h1 class="text-2xl font-bold text-gray-900 text-center sm:text-left">{{ doctorName }}</h1>

      <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2">
        <LabelBadge
          :label="`CRM ${doctor.doctor.mainCrm.number}-${doctor.doctor.mainCrm.uf}`"
          color="blue"
          :icon="null"
          size="small"
        />

        <LabelBadge
          v-if="crmStatus"
          :label="crmStatus.label"
          :color="crmStatus.color"
          :icon="null"
          size="small"
        />
      </div>

      <div class="flex items-center gap-1 text-sm text-gray-500">
        <v-icon size="16">{{ mdiOfficeBuildingOutline }}</v-icon>
        <span>{{ companiesLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiOfficeBuildingOutline } from '@mdi/js'

import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'
import type { DoctorDetailResponse } from '@/Network/Types/Responses/CompanyResponses'
import { classifyNpsScore, NEUTRAL_RING_COLOR } from '@/utils/nps'
import {
  getCrmStatusConfig,
  getInitials,
  resolveAvatarUrl,
  toTitleCase
} from '../../helpers/doctorStatus'

const props = defineProps<{
  doctor: DoctorDetailResponse
}>()

const doctorName = computed(() =>
  toTitleCase(props.doctor.doctor.user?.name || props.doctor.doctor.mainCrm.name)
)

const avatarSrc = computed(() => resolveAvatarUrl(props.doctor.doctor.user?.avatarUrl))

const initials = computed(() => getInitials(doctorName.value))

const AVATAR_SIZE = 80
const STROKE_WIDTH = 4
const radius = AVATAR_SIZE / 2 + STROKE_WIDTH / 2
const svgSize = radius * 2 + STROKE_WIDTH
const circumference = 2 * Math.PI * radius

const clampedScore = computed(() => {
  const score = props.doctor.doctor.npsScore
  if (score == null) return 0
  return Math.min(100, Math.max(0, (score + 100) / 2))
})

const offset = computed(() => circumference * (1 - clampedScore.value / 100))

const arcColor = computed(() => {
  const score = props.doctor.doctor.npsScore
  if (score == null) return NEUTRAL_RING_COLOR
  return classifyNpsScore(score).color
})

const crmStatus = computed(() => getCrmStatusConfig(props.doctor.doctor.mainCrm.status))

const companiesLabel = computed(() => {
  const count = props.doctor.companies.length
  return count === 1 ? '1 empresa vinculada' : `${count} empresas vinculadas`
})
</script>
